import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/navigation/Header';
import DashboardHeader from './components/DashboardHeader';
import TabNavigation from './components/TabNavigation';
import TripCard from './components/TripCard';
import FavoriteCard from './components/FavoriteCard';
import BookingCard from './components/BookingCard';
import RecommendationCard from './components/RecommendationCard';
import SettingsSection from './components/SettingsSection';
import EmptyState from './components/EmptyState';
import ReviewModal from '../bookings-dashboard/components/ReviewModal';

import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import Icon from '../../components/AppIcon';
import { useAuth } from '../../contexts/AuthContext';
import { getBookings } from '../../services/bookingService';
import { generatePersonalizedDestinations } from '../../services/recommendationService';


const UserDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('trips');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('recent');
  const [filterStatus, setFilterStatus] = useState('all');
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  const [bookingToReview, setBookingToReview] = useState(null);
  const [reviewSubmitted, setReviewSubmitted] = useState(false);
  const [aiRecommendations, setAiRecommendations] = useState([]);
  const [loadingRecommendations, setLoadingRecommendations] = useState(false);
  const [recommendationError, setRecommendationError] = useState(null);
  const [travelPersonality, setTravelPersonality] = useState(null);

  const { user: authUser } = useAuth();

  const [user] = useState({
    name: 'Maria Santos',
    email: 'maria.santos@email.com',
    id: 1
  });

  const mockTrips = [
  {
    id: 1,
    destination: 'Boracay Island Paradise',
    description: '5-day beach getaway with water sports and island hopping',
    image: "https://images.unsplash.com/photo-1722223072163-5b737b45ca2b",
    imageAlt: 'Aerial view of pristine white sand beach with crystal clear turquoise water and traditional Filipino boats in Boracay Island',
    startDate: '2026-03-15',
    duration: '5 days',
    travelers: 2,
    destinations: 3,
    status: 'upcoming'
  },
  {
    id: 2,
    destination: 'El Nido Adventure',
    description: 'Island hopping tour through hidden lagoons and limestone cliffs',
    image: "https://images.unsplash.com/photo-1611043718550-977bc9ae2429",
    imageAlt: 'Dramatic limestone karst cliffs rising from emerald green lagoon waters with traditional outrigger boat in El Nido Palawan',
    startDate: '2026-04-20',
    duration: '4 days',
    travelers: 4,
    destinations: 5,
    status: 'upcoming'
  },
  {
    id: 3,
    destination: 'Baguio Mountain Retreat',
    description: 'Cool mountain escape with pine forests and strawberry farms',
    image: "https://images.unsplash.com/photo-1607119539920-ee01a1b59ad7",
    imageAlt: 'Misty pine forest covered mountains with winding roads and traditional Filipino houses in Baguio City highlands',
    startDate: '2026-02-10',
    duration: '3 days',
    travelers: 3,
    destinations: 4,
    status: 'completed'
  },
  {
    id: 4,
    destination: 'Siargao Surf Experience',
    description: 'Surfing lessons and island exploration in the surfing capital',
    image: "https://images.unsplash.com/photo-1716038281322-41a8971bda92",
    imageAlt: 'Surfer riding perfect barrel wave with palm tree lined tropical beach and blue sky in Siargao Island',
    startDate: '2026-05-08',
    duration: '6 days',
    travelers: 2,
    destinations: 2,
    status: 'upcoming'
  },
  {
    id: 5,
    destination: 'Cebu Heritage Tour',
    description: 'Cultural exploration of historical sites and local cuisine',
    image: "https://images.unsplash.com/photo-1652414172010-4d9c57553672",
    imageAlt: 'Spanish colonial era stone church with ornate baroque architecture and bell tower in historic Cebu City',
    startDate: '2026-01-25',
    duration: '4 days',
    travelers: 2,
    destinations: 6,
    status: 'completed'
  }];


  const mockFavorites = [
  {
    id: 1,
    name: 'Nacpan Beach Resort',
    location: 'El Nido, Palawan',
    description: 'Secluded beachfront resort with pristine white sand and crystal clear waters',
    image: "https://images.unsplash.com/photo-1724598674807-6acb225b98f2",
    imageAlt: 'Luxury beachfront resort with thatched roof bungalows on white sand beach surrounded by palm trees and turquoise ocean',
    rating: 4.8,
    reviews: 342,
    priceRange: '3,500-8,000',
    badge: 'Hidden Gem'
  },
  {
    id: 2,
    name: 'Chocolate Hills Adventure Park',
    location: 'Bohol',
    description: 'Iconic geological formation with adventure activities and viewing decks',
    image: "https://images.unsplash.com/photo-1728042743743-e2a2abf35c47",
    imageAlt: 'Panoramic view of hundreds of cone-shaped hills covered in green grass with blue sky in Bohol Chocolate Hills',
    rating: 4.9,
    reviews: 567,
    priceRange: '500-1,200',
    badge: 'Featured'
  },
  {
    id: 3,
    name: 'Kawasan Falls Canyoneering',
    location: 'Cebu',
    description: 'Thrilling canyoneering experience ending at stunning turquoise waterfalls',
    image: "https://images.unsplash.com/photo-1578494688088-8260d62a79c1",
    imageAlt: 'Multi-tiered turquoise waterfall cascading into natural pool surrounded by lush tropical jungle vegetation in Kawasan Falls',
    rating: 4.7,
    reviews: 289,
    priceRange: '1,800-2,500'
  },
  {
    id: 4,
    name: 'Mayon Volcano View Lodge',
    location: 'Albay',
    description: 'Mountain lodge with spectacular views of the perfect cone volcano',
    image: "https://images.unsplash.com/photo-1661686178252-ed4798c5c8e7",
    imageAlt: 'Perfect cone-shaped Mayon Volcano with smoke plume rising from summit against blue sky with green rice fields in foreground',
    rating: 4.6,
    reviews: 198,
    priceRange: '2,200-5,500',
    badge: 'Hidden Gem'
  },
  {
    id: 5,
    name: 'Tubbataha Reef Diving',
    location: 'Palawan',
    description: 'World-class diving site with incredible marine biodiversity',
    image: "https://images.unsplash.com/photo-1692810123022-35e7d2044303",
    imageAlt: 'Underwater coral reef teeming with colorful tropical fish and sea turtles in crystal clear blue water at Tubbataha Reef',
    rating: 5.0,
    reviews: 423,
    priceRange: '15,000-25,000',
    badge: 'Featured'
  },
  {
    id: 6,
    name: 'Sagada Hanging Coffins',
    location: 'Mountain Province',
    description: 'Ancient burial site with traditional hanging coffins on cliff faces',
    image: "https://images.unsplash.com/photo-1600075622457-0c074335d3d7",
    imageAlt: 'Traditional wooden coffins hanging from limestone cliff face surrounded by misty mountains in Sagada burial site',
    rating: 4.5,
    reviews: 234,
    priceRange: '800-1,500'
  },
  {
    id: 7,
    name: 'Coron Island Hopping',
    location: 'Palawan',
    description: 'Explore hidden lagoons, pristine beaches, and WWII shipwrecks',
    image: "https://images.unsplash.com/photo-1611043718550-977bc9ae2429",
    imageAlt: 'Traditional Filipino outrigger boat floating in crystal clear turquoise lagoon surrounded by towering limestone cliffs in Coron',
    rating: 4.9,
    reviews: 512,
    priceRange: '2,500-4,000',
    badge: 'Featured'
  },
  {
    id: 8,
    name: 'Batanes Rolling Hills',
    location: 'Batanes',
    description: 'Scenic landscapes with rolling green hills and traditional stone houses',
    image: "https://images.unsplash.com/photo-1616668251403-5fff235c18ca",
    imageAlt: 'Vast rolling green hills with grazing cattle and traditional Ivatan stone houses under dramatic cloudy sky in Batanes',
    rating: 4.8,
    reviews: 367,
    priceRange: '3,000-6,500',
    badge: 'Hidden Gem'
  },
  {
    id: 9,
    name: 'Vigan Heritage Village',
    location: 'Ilocos Sur',
    description: 'UNESCO World Heritage Spanish colonial town with cobblestone streets',
    image: "https://images.unsplash.com/photo-1611097218756-121223090336",
    imageAlt: 'Historic Spanish colonial street with cobblestones and traditional horse-drawn carriages passing colorful ancestral houses in Vigan',
    rating: 4.7,
    reviews: 445,
    priceRange: '1,200-3,500'
  },
  {
    id: 10,
    name: 'Apo Island Marine Sanctuary',
    location: 'Negros Oriental',
    description: 'Protected marine reserve with sea turtles and vibrant coral gardens',
    image: "https://images.unsplash.com/photo-1599781451545-246440897dba",
    imageAlt: 'Large sea turtle swimming over colorful coral reef with schools of tropical fish in clear blue water at Apo Island',
    rating: 4.9,
    reviews: 389,
    priceRange: '1,500-2,800',
    badge: 'Featured'
  },
  {
    id: 11,
    name: 'Banaue Rice Terraces',
    location: 'Ifugao',
    description: '2000-year-old rice terraces carved into mountain slopes',
    image: "https://images.unsplash.com/photo-1639219384251-855f43df3238",
    imageAlt: 'Ancient terraced rice paddies carved into steep mountain slopes with traditional Filipino farmers working in green fields',
    rating: 4.8,
    reviews: 501,
    priceRange: '2,000-4,500'
  },
  {
    id: 12,
    name: 'Camiguin Island Volcano Trek',
    location: 'Camiguin',
    description: 'Active volcano hiking with hot springs and waterfalls',
    image: "https://images.unsplash.com/photo-1689697247762-f1cc3a2e6f4f",
    imageAlt: 'Smoking volcanic crater with lush green slopes and natural hot springs surrounded by tropical forest in Camiguin Island',
    rating: 4.6,
    reviews: 276,
    priceRange: '1,800-3,200',
    badge: 'Hidden Gem'
  }];


  const mockBookings = [
  {
    id: 1,
    name: 'Boracay Regency Beach Resort',
    location: 'Station 2, Boracay',
    description: 'Deluxe Ocean View Room with breakfast and beach access',
    image: "https://images.unsplash.com/photo-1624323118407-6356ba4ee473",
    imageAlt: 'Luxury beachfront hotel room with ocean view balcony overlooking white sand beach and turquoise water in Boracay resort',
    checkIn: '2026-03-15',
    checkOut: '2026-03-20',
    guests: 2,
    amount: 28500.00,
    bookingId: 'BRC2026031501',
    status: 'confirmed',
    paymentStatus: 'paid'
  },
  {
    id: 2,
    name: 'El Nido Island Hopping Tour',
    location: 'El Nido, Palawan',
    description: 'Full day tour package with lunch and snorkeling equipment',
    image: "https://images.unsplash.com/photo-1611043718550-977bc9ae2429",
    imageAlt: 'Traditional Filipino outrigger boat with tourists snorkeling in crystal clear lagoon surrounded by limestone cliffs in El Nido',
    checkIn: '2026-04-20',
    checkOut: '2026-04-20',
    guests: 4,
    amount: 6800.00,
    bookingId: 'ELN2026042001',
    status: 'confirmed',
    paymentStatus: 'paid'
  },
  {
    id: 3,
    name: 'Siargao Surf Camp Package',
    location: 'Cloud 9, Siargao',
    description: '5-day surf lessons with accommodation and board rental',
    image: "https://images.unsplash.com/photo-1713621802123-f87ba18c39f5",
    imageAlt: 'Surf instructor teaching beginner on surfboard in shallow tropical water with palm trees and surf camp in background',
    checkIn: '2026-05-08',
    checkOut: '2026-05-13',
    guests: 2,
    amount: 18900.00,
    bookingId: 'SIA2026050801',
    status: 'pending',
    paymentStatus: 'pending'
  }];


  const mockRecommendations = [
  {
    id: 1,
    name: 'Kalanggaman Island Day Trip',
    location: 'Leyte',
    description: 'Pristine sandbar island with crystal clear waters perfect for swimming',
    image: "https://images.unsplash.com/photo-1699221647562-cdc624b6ebae",
    imageAlt: 'Long white sandbar extending into turquoise ocean with palm trees and clear blue sky in Kalanggaman Island',
    rating: 4.9,
    popularity: '2.3k visitors',
    matchScore: 95,
    price: 2500,
    originalPrice: 3200,
    discount: 22,
    reason: 'Beach lover'
  },
  {
    id: 2,
    name: 'Taal Volcano Crater Trek',
    location: 'Batangas',
    description: 'Hike to the world\'s smallest active volcano with stunning lake views',
    image: "https://images.unsplash.com/photo-1605324443240-e0e496e19a1a",
    imageAlt: 'Active volcanic crater lake with steaming vents surrounded by green slopes and panoramic views in Taal Volcano',
    rating: 4.7,
    popularity: '1.8k visitors',
    matchScore: 88,
    price: 1800,
    originalPrice: 2400,
    discount: 25,
    reason: 'Adventure seeker'
  },
  {
    id: 3,
    name: 'Hundred Islands Hopping',
    location: 'Pangasinan',
    description: 'Explore mushroom-shaped islands with snorkeling and kayaking',
    image: "https://images.unsplash.com/photo-1722223072163-5b737b45ca2b",
    imageAlt: 'Aerial view of numerous small mushroom-shaped islands with white sand beaches in turquoise water at Hundred Islands',
    rating: 4.6,
    popularity: '3.1k visitors',
    matchScore: 92,
    price: 3200,
    reason: 'Island hopper'
  },
  {
    id: 4,
    name: 'Donsol Whale Shark Encounter',
    location: 'Sorsogon',
    description: 'Swim with gentle whale sharks in their natural habitat',
    image: "https://images.unsplash.com/photo-1712901394249-712c00ac3494",
    imageAlt: 'Massive whale shark swimming underwater with snorkelers in clear blue ocean water at Donsol marine sanctuary',
    rating: 5.0,
    popularity: '1.5k visitors',
    matchScore: 90,
    price: 4500,
    originalPrice: 5500,
    discount: 18,
    reason: 'Marine life enthusiast'
  },
  {
    id: 5,
    name: 'Sagada Cave Connection',
    location: 'Mountain Province',
    description: 'Underground river cave system with stunning rock formations',
    image: "https://images.unsplash.com/photo-1660636772713-cc1c7f2516a3",
    imageAlt: 'Underground limestone cave with dramatic stalactites and stalagmites illuminated by headlamps in Sagada cave system',
    rating: 4.8,
    popularity: '2.7k visitors',
    matchScore: 85,
    price: 1500,
    reason: 'Adventure seeker'
  },
  {
    id: 6,
    name: 'Camiguin Sunken Cemetery',
    location: 'Camiguin',
    description: 'Snorkel over submerged cemetery with giant cross marker',
    image: "https://images.unsplash.com/photo-1665386635374-b742cecd83d6",
    imageAlt: 'Large white cross monument rising from turquoise ocean water marking underwater cemetery in Camiguin Island',
    rating: 4.5,
    popularity: '1.2k visitors',
    matchScore: 87,
    price: 2200,
    originalPrice: 2800,
    discount: 21,
    reason: 'Unique experiences'
  }];


  const sortOptions = [
  { value: 'recent', label: 'Most Recent' },
  { value: 'oldest', label: 'Oldest First' },
  { value: 'name', label: 'Name (A-Z)' },
  { value: 'price', label: 'Price (Low to High)' }];


  const statusOptions = [
  { value: 'all', label: 'All Status' },
  { value: 'upcoming', label: 'Upcoming' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' }];


  const handleEditTrip = (tripId) => {
    console.log('Edit trip:', tripId);
  };

  const handleShareTrip = (tripId) => {
    console.log('Share trip:', tripId);
  };

  const handleDeleteTrip = (tripId) => {
    console.log('Delete trip:', tripId);
  };

  const handleRemoveFavorite = (favoriteId) => {
    console.log('Remove favorite:', favoriteId);
  };

  const handleViewDetails = (id) => {
    console.log('View details:', id);
  };

  const handleCancelBooking = (bookingId) => {
    console.log('Cancel booking:', bookingId);
  };

  const handleSaveRecommendation = (recommendationId) => {
    console.log('Save recommendation:', recommendationId);
  };

  const handleUpdateProfile = (formData) => {
    console.log('Update profile:', formData);
  };

  const handleCreateTrip = () => {
    navigate('/homepage');
  };

  const handleDiscoverDestinations = () => {
    navigate('/homepage');
  };

  const handleBrowseHotels = () => {
    navigate('/homepage');
  };

  const handleWriteReview = (booking) => {
    setBookingToReview(booking);
    setIsReviewModalOpen(true);
  };

  const handleReviewSubmitted = () => {
    setReviewSubmitted(true);
    setTimeout(() => setReviewSubmitted(false), 3000);
  };

  // Load AI-powered personalized recommendations
  useEffect(() => {
    const loadPersonalizedRecommendations = async () => {
      if (activeTab !== 'recommendations' || !authUser?.id) return;
      
      setLoadingRecommendations(true);
      setRecommendationError(null);
      
      try {
        // Fetch user's booking history
        const bookingHistory = await getBookings(authUser?.id);
        
        // Prepare user profile data
        const userProfile = {
          name: authUser?.user_metadata?.full_name || authUser?.email?.split('@')?.[0],
          email: authUser?.email,
          interests: ['beaches', 'adventure', 'culture', 'nature'],
          travelStyle: 'explorer'
        };
        
        // Prepare preferences
        const preferences = {
          budgetRange: 'medium',
          travelStyle: 'balanced',
          interests: ['beaches', 'adventure', 'culture', 'nature']
        };
        
        // Available destinations from favorites/mock data
        const availableDestinations = mockFavorites?.map(fav => ({
          id: fav?.id,
          name: fav?.name,
          location: fav?.location,
          description: fav?.description,
          category: fav?.badge || 'destination',
          priceRange: fav?.priceRange,
          rating: fav?.rating,
          highlights: [fav?.description]
        }));
        
        // Generate AI recommendations
        const result = await generatePersonalizedDestinations({
          userProfile,
          bookingHistory,
          availableDestinations,
          preferences
        });
        
        // Transform AI recommendations to match RecommendationCard format
        const transformedRecommendations = result?.recommendations?.map(rec => {
          const sourceDestination = mockFavorites?.find(f => f?.id?.toString() === rec?.destinationId);
          return {
            id: rec?.destinationId,
            name: rec?.name,
            location: rec?.location,
            description: rec?.uniqueAppeal,
            image: sourceDestination?.image || mockFavorites?.[0]?.image,
            imageAlt: sourceDestination?.imageAlt || `${rec?.name} - ${rec?.location}`,
            rating: sourceDestination?.rating || 4.5,
            popularity: `${Math.floor(Math.random() * 3000 + 1000)} visitors`,
            matchScore: rec?.personalizationScore,
            price: rec?.estimatedBudget,
            originalPrice: rec?.estimatedBudget * 1.2,
            discount: 15,
            reason: rec?.reasoning?.substring(0, 50) + '...'
          };
        });
        
        setAiRecommendations(transformedRecommendations || []);
        setTravelPersonality(result?.travelPersonality);
      } catch (error) {
        console.error('Error loading AI recommendations:', error);
        setRecommendationError(error?.message || 'Failed to load personalized recommendations');
        // Fallback to mock recommendations
        setAiRecommendations(mockRecommendations);
      } finally {
        setLoadingRecommendations(false);
      }
    };
    
    loadPersonalizedRecommendations();
  }, [activeTab, authUser?.id]);

  const filteredTrips = mockTrips?.filter((trip) => {
    const matchesSearch = trip?.destination?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
    trip?.description?.toLowerCase()?.includes(searchQuery?.toLowerCase());
    const matchesStatus = filterStatus === 'all' || trip?.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const sortedTrips = [...filteredTrips]?.sort((a, b) => {
    if (sortBy === 'recent') {
      return new Date(b.startDate) - new Date(a.startDate);
    }
    if (sortBy === 'oldest') {
      return new Date(a.startDate) - new Date(b.startDate);
    }
    if (sortBy === 'name') {
      return a?.destination?.toLowerCase()?.localeCompare(b?.destination?.toLowerCase());
    }
    if (sortBy === 'price') {
      return a?.price - b?.price;
    }
  });

  const filteredBookings = mockBookings?.filter((booking) =>
  booking?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
  booking?.location?.toLowerCase()?.includes(searchQuery?.toLowerCase())
  );

  const sortedBookings = [...filteredBookings]?.sort((a, b) => {
    if (sortBy === 'recent') {
      return new Date(b.checkIn) - new Date(a.checkIn);
    }
    if (sortBy === 'oldest') {
      return new Date(a.checkIn) - new Date(b.checkIn);
    }
    if (sortBy === 'name') {
      return a?.name?.toLowerCase()?.localeCompare(b?.name?.toLowerCase());
    }
    if (sortBy === 'price') {
      return a?.amount - b?.amount;
    }
  });

  const filteredFavorites = mockFavorites?.filter((favorite) =>
  favorite?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
  favorite?.location?.toLowerCase()?.includes(searchQuery?.toLowerCase())
  );

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [activeTab]);

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="main-content">
        <DashboardHeader user={user} activeTab={activeTab} />
        
        <TabNavigation activeTab={activeTab} onTabChange={setActiveTab} />

        {/* Success Message */}
        {reviewSubmitted && (
          <div className="mb-6 flex items-center gap-3 p-4 rounded-xl bg-success/10 border border-success/20">
            <Icon name="CheckCircle" size={20} className="text-success flex-shrink-0" />
            <p className="text-sm font-medium text-success">
              Thank you for your review! Your feedback helps other travelers.
            </p>
          </div>
        )}

        {activeTab !== 'settings' &&
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8">
            <div className="flex flex-col md:flex-row gap-4 mb-6">
              <div className="flex-1">
                <Input
                type="search"
                placeholder={`Search ${activeTab}...`}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e?.target?.value)} />

              </div>
              <div className="flex gap-3">
                {activeTab === 'trips' &&
              <Select
                options={statusOptions}
                value={filterStatus}
                onChange={setFilterStatus}
                className="w-full md:w-48" />

              }
                <Select
                options={sortOptions}
                value={sortBy}
                onChange={setSortBy}
                className="w-full md:w-48" />

              </div>
            </div>
          </div>
        }

        {activeTab === 'trips' &&
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 pb-20 md:pb-8">
            {sortedTrips?.length > 0 ?
          <>
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl md:text-2xl font-bold text-foreground">
                    {filterStatus === 'all' ? 'All Trips' : `${filterStatus?.charAt(0)?.toUpperCase() + filterStatus?.slice(1)} Trips`}
                    <span className="text-muted-foreground ml-2">({sortedTrips?.length})</span>
                  </h2>
                  <Button
                variant="default"
                onClick={handleCreateTrip}
                iconName="Plus"
                iconPosition="left">

                    New Trip
                  </Button>
                </div>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {sortedTrips?.map((trip) =>
              <TripCard
                key={trip?.id}
                trip={trip}
                onEdit={handleEditTrip}
                onShare={handleShareTrip}
                onDelete={handleDeleteTrip} />

              )}
                </div>
              </> :

          <EmptyState type="trips" onAction={handleCreateTrip} />
          }
          </div>
        }

        {activeTab === 'favorites' &&
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 pb-20 md:pb-8">
            {filteredFavorites?.length > 0 ?
          <>
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl md:text-2xl font-bold text-foreground">
                    Saved Destinations
                    <span className="text-muted-foreground ml-2">({filteredFavorites?.length})</span>
                  </h2>
                </div>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {filteredFavorites?.map((favorite) =>
              <FavoriteCard
                key={favorite?.id}
                favorite={favorite}
                onRemove={handleRemoveFavorite}
                onViewDetails={handleViewDetails} />

              )}
                </div>
              </> :

          <EmptyState type="favorites" onAction={handleDiscoverDestinations} />
          }
          </div>
        }

        {activeTab === 'bookings' &&
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 pb-20 md:pb-8">
            {sortedBookings?.length > 0 ?
          <>
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl md:text-2xl font-bold text-foreground">
                    My Bookings
                    <span className="text-muted-foreground ml-2">({sortedBookings?.length})</span>
                  </h2>
                </div>
                <div className="space-y-4">
                  {sortedBookings?.map((booking) =>
              <BookingCard
                key={booking?.id}
                booking={booking}
                onViewDetails={handleViewDetails}
                onCancel={handleCancelBooking}
                onWriteReview={handleWriteReview} />

              )}
                </div>
              </> :

          <EmptyState type="bookings" onAction={handleBrowseHotels} />
          }
          </div>
        }

        {activeTab === 'recommendations' &&
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 pb-20 md:pb-8">
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-2">
                <Icon name="Sparkles" size={24} className="text-primary" />
                <h2 className="text-xl md:text-2xl font-bold text-foreground">
                  AI-Powered Recommendations
                </h2>
              </div>
              <p className="text-sm md:text-base text-muted-foreground">
                Personalized destinations based on your travel history and preferences
              </p>
              {travelPersonality && (
                <div className="mt-4 p-4 bg-primary/5 rounded-lg border border-primary/10">
                  <h3 className="text-sm font-semibold text-foreground mb-2">Your Travel Personality</h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-xs">
                    <div>
                      <span className="text-muted-foreground">Style:</span>
                      <span className="ml-2 font-medium text-foreground">{travelPersonality?.primaryStyle}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Budget:</span>
                      <span className="ml-2 font-medium text-foreground">{travelPersonality?.budgetProfile}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Adventure:</span>
                      <span className="ml-2 font-medium text-foreground">{travelPersonality?.adventureLevel}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            {loadingRecommendations && (
              <div className="flex flex-col items-center justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mb-4"></div>
                <p className="text-muted-foreground">Generating personalized recommendations...</p>
              </div>
            )}
            
            {recommendationError && (
              <div className="mb-6 p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
                <div className="flex items-start gap-3">
                  <Icon name="AlertCircle" size={20} className="text-destructive flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-destructive mb-1">Unable to generate AI recommendations</p>
                    <p className="text-xs text-muted-foreground">{recommendationError}</p>
                    <p className="text-xs text-muted-foreground mt-1">Showing default recommendations instead.</p>
                  </div>
                </div>
              </div>
            )}
            
            {!loadingRecommendations && aiRecommendations?.length > 0 && (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {aiRecommendations?.map((recommendation) =>
              <RecommendationCard
                key={recommendation?.id}
                recommendation={recommendation}
                onSave={handleSaveRecommendation}
                onViewDetails={handleViewDetails} />

              )}
              </div>
            )}
            
            {!loadingRecommendations && aiRecommendations?.length === 0 && !recommendationError && (
              <EmptyState type="recommendations" onAction={handleDiscoverDestinations} />
            )}
          </div>
        }

        {activeTab === 'settings' &&
        <SettingsSection user={user} onUpdateProfile={handleUpdateProfile} />
        }
      </div>

      {/* Review Modal */}
      <ReviewModal
        isOpen={isReviewModalOpen}
        onClose={() => setIsReviewModalOpen(false)}
        booking={bookingToReview}
        onReviewSubmitted={handleReviewSubmitted}
      />
    </div>);

};

export default UserDashboard;